import { useCallback, useEffect, useState } from 'react';
import { stateSetter } from '@/utils';
import { IVersion, VersionReturnType } from './types';
import {
	fetchSingleVersion,
	fetchVersionQuery,
	fetchVersions,
	setRequiredVersion as setRequiredVersionAction
} from './actions';

// Fallback stabil ca identitate pentru `data` neîncărcat — un `[]` nou la fiecare
// randare re-declanșează în lanț efectele consumatorilor cu dependență pe `data`
// („Maximum update depth exceeded").
const EMPTY_LIST: never[] = [];

export function useVersion<T extends IVersion | IVersion[]>(
	getOnRender: boolean | undefined = false,
	parsId: number | string | undefined = undefined,
	queryParam: string | undefined = undefined,
	/**
	 * Paginarea INIȚIALĂ (de regulă cea din URL, via useListQueryState). Fără ea,
	 * starea internă pornește pe 0/10 și primul fetch pleacă cu valori greșite, urmat
	 * de un al doilea după sincronizarea din efectele-punte — două call-uri și un race
	 * pe răspunsuri. Doar valoarea de la primul render contează.
	 */
	initialPagination: { page: number; items: number } | undefined = undefined
): VersionReturnType<T> {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [loadingCTA, setLoadingCTA] = useState<boolean>(false);
	const [page, setPage] = useState<number>(initialPagination?.page ?? 0);
	const [items, setItems] = useState<number>(initialPagination?.items ?? 10);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(1);
	const [refresh, setRefresh] = useState({});

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	const getVersions = useCallback(
		async (pageParam: number, itemsParam: number) => {
			try {
				setLoading(true);
				const response = await fetchVersions(pageParam, itemsParam, handleResponse);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const getVersionsQuery = useCallback(
		async (query: string, queryPage: number, queryItems: number) => {
			try {
				setLoading(true);
				const response = await fetchVersionQuery(
					query,
					queryPage,
					queryItems,
					handleResponse
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
				setLoadingCTA(false);
			}
		},
		 
		[setData]
	);

	const getSingleVersion = useCallback(
		async (id: number | string) => {
			try {
				setLoading(true);
				const response = await fetchSingleVersion(id);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const setRequiredVersion = useCallback(
		async (id: number | string, onSuccess?: () => void) => {
			try {
				setLoadingCTA(true);
				const response = await setRequiredVersionAction(id);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingCTA(false);
			}
		},
		 
		[setData]
	);

	useEffect(() => {
		if (getOnRender && queryParam === undefined) {
			getVersions(page, items);
		}
		 
	}, [queryParam, getOnRender, page, items, refresh]);

	useEffect(() => {
		if (parsId) {
			getSingleVersion(parsId);
		}
		 
	}, [parsId]);

	useEffect(() => {
		if (queryParam && parsId === undefined) {
			getVersionsQuery(queryParam, page, items);
		}
		 
	}, [queryParam, parsId, page, items]);

	return {
			data: data || (EMPTY_LIST as IVersion[] as T),
			loading,
			loadingCTA,
			setData: setData as stateSetter<T>,
			setRequiredVersion,
			getVersions,
			setRefresh,
		items,
		page,
		setItems,
		setPage,
		totalItems,
		totalPages
	};
}
